import { Dispatch } from 'redux';
import SuppressionListsController from '../controllers/SuppressionListsController';
import commonHandler from '../errorHandlers/common-handler';
import { SuppressionList } from '../model/SuppressionListModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class SuppressionListsCRUDActions extends CrudActionContainer {
  resetStore: string;

  constructor() {
    super('SUPPRESSION_LISTS');

    this.resetStore = this.makeActionKey('RESET_STORE');
  }

  create(facebookPageId: number, suppression: Partial<SuppressionList>) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response: SuppressionList =
          await SuppressionListsController.create(facebookPageId, suppression);
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveById(facebookPageId: number, id: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await SuppressionListsController.getById(
          facebookPageId,
          id,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAll(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response = await SuppressionListsController.getAll(
          facebookPageId,
        );

        dispatch({
          type: this.retrievedAllAction,
          entities: response,
        });

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  update(
    facebookPageId: number,
    suppression: { id: number } & Partial<SuppressionList>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response = await SuppressionListsController.update(
          facebookPageId,
          suppression,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  deleteList(
    facebookPageId: number,
    suppression: { id: number } & Partial<SuppressionList>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));

      try {
        const response = await SuppressionListsController.deleteList(
          facebookPageId,
          suppression.id,
        );
        dispatch(this.deleteLocal(suppression));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  invalidateStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const suppressionListsCRUDActions = new SuppressionListsCRUDActions();

export default suppressionListsCRUDActions;

export const useSuppressionListsCRUDActions = getActionHookBuilder(
  suppressionListsCRUDActions,
  [
    'retrieveAll',
    'retrieveById',
    'create',
    'update',
    'deleteList',
    'invalidateStore',
  ],
);
